// src/components/landing/BeforeAfterSection.tsx
import React from 'react'
import { ArrowRight, ArrowDown } from 'lucide-react'
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll'

const BeforeAfterSection: React.FC = () => {
  const revealRef = useRevealOnScroll<HTMLDivElement>()

  return (
    <section
      id="antes-depois"
      className="py-20 bg-cream-100 dark:bg-ink-700"
      aria-labelledby="antes-depois-titulo"
    >
      <div ref={revealRef} className="container mx-auto px-4">
        <div data-reveal-item className="text-center mb-12 max-w-3xl mx-auto">
          <p className="flex items-center justify-center gap-3 font-mono text-caderno-xs uppercase tracking-widest text-ink-400 dark:text-ink-300 mb-4">
            <span className="text-terra-500">01</span>
            <span aria-hidden="true" className="w-8 h-px bg-cream-400 dark:bg-ink-500" />
            A virada
          </p>
          <h2
            id="antes-depois-titulo"
            className="text-3xl md:text-4xl font-display text-ink-900 dark:text-cream-50 mb-4"
          >
            Domingo, 22h47, café frio.{' '}
            <span className="italic font-normal text-terra-500">Não precisa mais ser assim.</span>
          </h2>
          <p className="text-lg text-ink-500 dark:text-cream-300">
            Você conhece a cena: caderno rabiscado, gabarito pela metade, impressão na segunda de
            manhã. A mesma prova, do outro jeito:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-4 items-center max-w-5xl mx-auto">
          {/* Antes: rabisco em caderno */}
          <div
            data-reveal-item
            className="relative bg-cream-50 dark:bg-ink-900 rounded-caderno-sm p-6 shadow-caderno-md border border-cream-400 dark:border-ink-500 -rotate-1"
            aria-label="Prova rabiscada em caderno (antes)"
          >
            <span
              aria-hidden="true"
              className="absolute -top-4 right-4 font-hand text-xl text-blood-500 -rotate-2"
            >
              domingo, 22h47
            </span>
            <div className="font-hand text-2xl text-ink-700 dark:text-cream-100 leading-relaxed">
              <div className="border-b border-cream-400 pb-2 mb-3">Prova de Matemática · 9º A</div>
              <div>1) Calcule a área…</div>
              <div>2) ___________</div>
              <div>3) Um polígono…</div>
              <div>4) ___________</div>
              <div className="mt-4 text-base text-blood-500">↳ ainda falta gabarito</div>
            </div>
          </div>

          {/* Seta */}
          <div
            data-reveal-item
            className="reveal-delay-1 flex items-center justify-center text-terra-500"
            aria-hidden="true"
          >
            <ArrowRight className="hidden md:block w-10 h-10" />
            <ArrowDown className="md:hidden w-10 h-10" />
          </div>

          {/* Depois: PDF profissional */}
          <div
            data-reveal-item
            className="reveal-delay-2 relative bg-surface dark:bg-ink-900 rounded-caderno-sm p-6 shadow-caderno-lg border border-cream-400 dark:border-ink-500"
            aria-label="Avaliação profissional gerada (depois)"
          >
            <span
              aria-hidden="true"
              className="absolute -top-4 right-4 font-hand text-xl text-moss-500 rotate-1"
            >
              pronta em 5 minutos ✓
            </span>
            <div className="border-b-2 border-ink-900 dark:border-cream-100 pb-2 mb-4">
              <div className="font-display text-lg text-ink-900 dark:text-cream-50">AVALIAÇÃO · Matemática</div>
              <div className="font-mono text-xs text-ink-500 dark:text-cream-300 mt-1">9º ano · 2º Bimestre · 10 questões</div>
            </div>
            <ol className="space-y-3 text-sm text-ink-700 dark:text-cream-200">
              <li><strong>1.</strong> Calcule a área do triângulo abaixo, sabendo que sua base mede 8 cm e altura 6 cm.</li>
              <li><strong>2.</strong> Um polígono regular tem 12 lados. Quanto mede cada ângulo interno?</li>
              <li><strong>3.</strong> Qual o volume de um cilindro de raio 4 cm e altura 10 cm?</li>
              <li className="text-ink-400 dark:text-ink-300">+ 7 questões e gabarito completo</li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BeforeAfterSection
